import axios from 'axios';
import { emitToast } from '../context/ToastContext';
import { API_BASE } from '../config/runtimeConfig';
import { captureApiFailure } from '../observability';
import { getFriendlyErrorMessage, getRawErrorDetail } from '../utils/errorMessages';

// Persist tokens in sessionStorage (cleared on tab close — safer than localStorage)
const getStoredToken = () => {
  try { return sessionStorage.getItem('su_access_token'); } catch { return null; }
};

const getStoredRefreshToken = () => {
  try { return sessionStorage.getItem('su_refresh_token'); } catch { return null; }
};

// Correlation id — ties a support case to the backend log / Sentry event.
const newCorrelationId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* crypto unavailable */ }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const setStoredToken = (token) => {
  try { if (token) sessionStorage.setItem('su_access_token', token); else sessionStorage.removeItem('su_access_token'); } catch { /* sessionStorage unavailable */ }
};

export const setStoredRefreshToken = (token) => {
  try { if (token) sessionStorage.setItem('su_refresh_token', token); else sessionStorage.removeItem('su_refresh_token'); } catch { /* sessionStorage unavailable */ }
};

// Track in-flight refresh so we don't fire multiple refresh requests concurrently.
let refreshPromise = null;

const refreshAndRetry = async (originalRequest) => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token'));
  }

  // If a refresh is already in-flight, chain off it instead of starting a second one.
  if (!refreshPromise) {
    refreshPromise = api.post('auth/refresh', { refreshToken })
      .then(response => {
        const { accessToken, refreshToken: newRefresh } = response.data;
        if (accessToken) setStoredToken(accessToken);
        if (newRefresh) setStoredRefreshToken(newRefresh);
        return accessToken;
      })
      .catch(() => {
        setStoredToken(null);
        setStoredRefreshToken(null);
        return null;
      })
      .finally(() => { refreshPromise = null; });
  }

  const newToken = await refreshPromise;
  if (!newToken) {
    return Promise.reject(new Error('Refresh failed'));
  }

  originalRequest.headers.Authorization = `Bearer ${newToken}`;
  originalRequest._retry = true;
  return api(originalRequest);
};

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach stored token to every request
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (!config.headers['X-Correlation-Id']) {
    config.headers['X-Correlation-Id'] = newCorrelationId();
  }
  return config;
});

// Add a response interceptor to handle errors globally
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    const correlationId =
      error.response?.headers?.['x-correlation-id'] ||
      error.config?.headers?.['X-Correlation-Id'];
    error.correlationId = correlationId;

    error.rawDetail = getRawErrorDetail(error);

    captureApiFailure({
      method: error.config?.method,
      path: error.config?.url,
      status,
      correlationId,
      message: error.response?.data?.message || error.response?.data?.title || error.message,
    });

    if (status === 401 && !originalRequest._retry) {
      try {
        const newToken = await refreshAndRetry(originalRequest);
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch {
        // Refresh failed — redirect to login
        setStoredToken(null);
        setStoredRefreshToken(null);
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login?expired=true';
        }
        return Promise.reject(error);
      }
    }

    const message = getFriendlyErrorMessage(error);
    error.normalizedMessage = message;

    const operatorMessage = correlationId ? `${message}\nRef: ${correlationId}` : message;

    const detail = {
      status,
      raw: error.rawDetail?.message || null,
      correlationId,
    };

    if (status >= 400 && status !== 401) {
      emitToast({ type: 'error', message: operatorMessage, detail });
    } else if (!error.response && error.request) {
      emitToast({ type: 'error', message: operatorMessage, detail });
    }

    return Promise.reject(error);
  }
);

export default api;
